import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase/client';

const AuthCallback = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Confirming your email...');

  useEffect(() => {
    // Handle PKCE flow: PASSWORD_RECOVERY event fires after code exchange
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        navigate('/reset-password');
      }
    });

    const handleCallback = async () => {
      try {
        // Parse URL for error or success
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const queryParams = new URLSearchParams(window.location.search);

        // Check for password recovery type (implicit flow — type is in URL)
        const type = hashParams.get('type') || queryParams.get('type');

        if (type === 'recovery') {
          console.log('Password recovery detected, redirecting to reset password page');
          setStatus('success');
          setMessage('Redirecting to password reset...');
          subscription.unsubscribe();
          setTimeout(() => navigate('/reset-password'), 500);
          return;
        }

        // Check for errors from Supabase
        const error = hashParams.get('error') || queryParams.get('error');
        const errorDescription = hashParams.get('error_description') ||
                                queryParams.get('error_description');

        if (error) {
          console.error('Auth callback error:', error, errorDescription);
          setStatus('error');
          setMessage(errorDescription || 'Email confirmation failed. Please try again.');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Get the current session to check if user is authenticated
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          setStatus('error');
          setMessage('Unable to verify your session. Please login.');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }
        
        if (session) {
          // User is authenticated
          setStatus('success');
          setMessage('Email confirmed! Setting up your account...');
          
          // Check if profile is completed
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('profile_completed, company_id, full_name')
            .eq('id', session.user.id)
            .single();

          if (userError && userError.code !== 'PGRST116') {
            // Error other than "not found"
            console.error('Error fetching user data:', userError);
          }

          // Route based on profile status
          setTimeout(() => {
            if (userData?.profile_completed) {
              console.log('Profile already completed, redirecting to dashboard');
              navigate('/dashboard');
            } else {
              console.log('Profile incomplete, redirecting to complete-profile');
              navigate('/complete-profile');
            }
          }, 1500);
        } else {
          // No session but email might be confirmed
          // This can happen if confirmation was done in a different browser
          setStatus('success');
          setMessage('Email confirmed! Please login to continue.');
          setTimeout(() => navigate('/login'), 2000);
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setStatus('error');
        setMessage('Something went wrong. Please try logging in.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();

    return () => subscription.unsubscribe();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">{message}</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="text-green-500 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Success!</h2>
              <p className="text-gray-600">{message}</p>
              <div className="mt-4">
                <div className="inline-flex rounded-full bg-green-100 p-1">
                  <div className="inline-flex items-center justify-center rounded-full bg-green-200 p-0.5">
                    <div className="h-1.5 w-1.5 rounded-full bg-green-600 animate-pulse"></div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">Redirecting...</p>
              </div>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="text-red-500 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Oops!</h2>
              <p className="text-gray-600 mb-4">{message}</p>
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Go to Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;